import * as THREE from 'three';

/**
 * Fog of war system that obscures areas outside the player's vision.
 *
 * Implementation approach:
 * - Creates a large circular gradient overlay above the game plane
 * - The gradient is dark at the edges (obscured) and transparent at the center (visible)
 * - Follows the player position and scales based on vision radius
 * - Uses a custom shader for smooth radial gradient falloff
 *
 * Performance:
 * - Single plane mesh with custom shader material
 * - Only updates position each frame (very cheap)
 * - Radius changes are rare (only on part swap)
 */
export class FogOfWar {
  private fogMesh: THREE.Mesh;
  private visionRadius: number = 10;
  private scene: THREE.Scene;

  /**
   * Create the fog of war overlay.
   *
   * @param scene - Three.js scene to add the fog mesh to
   * @param initialRadius - Starting vision radius in world units
   */
  constructor(scene: THREE.Scene, initialRadius: number = 10) {
    this.scene = scene;
    this.visionRadius = initialRadius;

    // Create fog mesh with custom shader
    this.fogMesh = this.createFogMesh();

    // Add to scene at elevated position (above game plane, below UI)
    this.fogMesh.position.y = 5;
    this.scene.add(this.fogMesh);
  }

  /**
   * Create the fog mesh with custom radial gradient shader.
   *
   * The shader creates a circular gradient from transparent (center) to opaque black (edges).
   * This gives a smooth falloff for the fog of war effect.
   *
   * @returns Mesh with custom shader material
   */
  private createFogMesh(): THREE.Mesh {
    // Large plane to cover the visible area
    // Size is generous to ensure edges aren't visible during gameplay
    const planeSize = 100;
    const geometry = new THREE.PlaneGeometry(planeSize, planeSize);

    // Custom shader for radial gradient
    const material = new THREE.ShaderMaterial({
      uniforms: {
        // Center of the vision circle in world space (updated each frame)
        center: { value: new THREE.Vector2(0, 0) },
        // Radius of clear vision in world units
        visionRadius: { value: this.visionRadius },
        // Falloff distance - how quickly fog fades in
        falloffDistance: { value: 3.0 },
        // Fog color (dark to match scene background)
        fogColor: { value: new THREE.Color(0x0a0a14) },
        // Fog opacity (0 = transparent, 1 = opaque)
        fogOpacity: { value: 0.9 },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vWorldPosition;

        void main() {
          vUv = uv;
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: `
        uniform vec2 center;
        uniform float visionRadius;
        uniform float falloffDistance;
        uniform vec3 fogColor;
        uniform float fogOpacity;

        varying vec2 vUv;
        varying vec3 vWorldPosition;

        void main() {
          // Calculate distance from this fragment to the center of vision
          vec2 fragPos = vWorldPosition.xz;
          float dist = distance(fragPos, center);

          // Calculate fog alpha based on distance
          // - Inside visionRadius: fully transparent (alpha = 0)
          // - Outside visionRadius + falloffDistance: fully opaque (alpha = fogOpacity)
          // - In between: smooth gradient
          float innerRadius = visionRadius;
          float outerRadius = visionRadius + falloffDistance;

          float alpha = smoothstep(innerRadius, outerRadius, dist);
          alpha *= fogOpacity;

          gl_FragColor = vec4(fogColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });

    // Rotate to horizontal (facing down from above)
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;

    return mesh;
  }

  /**
   * Update the vision radius.
   *
   * This is called when the player swaps head parts.
   * Updates the shader uniform to scale the fog circle.
   *
   * @param radius - New vision radius in world units
   */
  setVisionRadius(radius: number): void {
    this.visionRadius = radius;
    const material = this.fogMesh.material as THREE.ShaderMaterial;
    material.uniforms.visionRadius.value = radius;
  }

  /**
   * Update the center position of the vision circle.
   *
   * This is called every frame to follow the player.
   * Updates the shader uniform to move the clear area.
   *
   * @param x - World X position
   * @param y - World Y position (actually Z in Three.js due to top-down view)
   */
  updatePosition(x: number, y: number): void {
    const material = this.fogMesh.material as THREE.ShaderMaterial;
    material.uniforms.center.value.set(x, y);
  }

  /**
   * Clean up resources when destroying the fog system.
   */
  destroy(): void {
    this.scene.remove(this.fogMesh);
    if (this.fogMesh.geometry) {
      this.fogMesh.geometry.dispose();
    }
    if (this.fogMesh.material) {
      (this.fogMesh.material as THREE.Material).dispose();
    }
  }
}
